/**
 * TemplateDownloader Component
 *
 * Provides buttons to download CSV templates for OrgUnits and Locations
 * with sample data and proper formatting.
 *
 * @module ImportExportTab/components/TemplateDownloader
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { generateOrgUnitCSV, generateLocationCSV } from '../../utils/csvTemplates';

/**
 * TemplateDownloader Component
 *
 * Renders download buttons for CSV templates with sample data.
 * Downloads include:
 * - Proper headers
 * - 2-3 sample rows with valid data
 * - UTF-8 BOM for Excel compatibility
 *
 * @component
 * @returns {React.ReactElement} Template downloader UI
 */
function TemplateDownloader() {
  /**
   * Handle download of OrgUnit CSV template
   *
   * Generates CSV with sample organizational unit data and triggers
   * browser download with appropriate filename and mime type.
   */
  const handleDownloadOrgUnits = () => {
    try {
      const csv = generateOrgUnitCSV();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `orgunit-template-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading OrgUnit template:', error);
      // Fail silently in production - error is logged
    }
  };

  /**
   * Handle download of Location CSV template
   *
   * Generates CSV with sample location data and triggers
   * browser download with appropriate filename and mime type.
   */
  const handleDownloadLocations = () => {
    try {
      const csv = generateLocationCSV();
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `location-template-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading Location template:', error);
      // Fail silently in production - error is logged
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Download CSV Templates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Download templates with sample data to see the required format
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <AdminButton
          startIcon={<DownloadIcon />}
          onClick={handleDownloadOrgUnits}
        >
          Download OrgUnits Template
        </AdminButton>

        <AdminButton
          startIcon={<DownloadIcon />}
          onClick={handleDownloadLocations}
        >
          Download Locations Template
        </AdminButton>
      </Box>
    </Box>
  );
}

export default TemplateDownloader;
